import {
    AiContextualAiAnswer,
    AiContextualAiRequest,
    AiContextualAiResponse,
    AiContextualAiStage,
    AiContextualAiWorkflow,
    AiPositionDescriptionResponse,
    AiTemplatePreview,
} from '../../apiClients/AiTemplateClient';
import { ContextualAiAction, ContextualAiTurnResult, ContextualAiWorkflow } from './contextualAiTypes';

export const contextualStageForAction = (action: ContextualAiAction): AiContextualAiStage => {
    if (action.startsWith('coverage-'))
        return 'ReviewCoverage';
    if (action === 'generate-now')
        return 'Generate';
    return 'Discover';
};

/** Backend v1 supports only these two workflow discriminators. */
export const workflowWireName = (workflow: ContextualAiWorkflow): AiContextualAiWorkflow => ({
    'position-description': 'PositionDescription',
    'onboarding-position': 'PositionDescription',
    'template-generation': 'TemplateGeneration',
    'template-editor': 'TemplateGeneration',
    'onboarding-success-profile': 'TemplateGeneration',
    'ongoing-assessment': 'PositionDescription',
}[workflow] as AiContextualAiWorkflow);

export const buildContextualRequest = (
    workflow: ContextualAiWorkflow,
    action: ContextualAiAction,
    request: Omit<AiContextualAiRequest, 'Workflow' | 'Stage' | 'Answers'> & { Answers?: AiContextualAiAnswer[] },
): AiContextualAiRequest => ({
    ...request,
    Workflow: workflowWireName(workflow),
    Stage: contextualStageForAction(action),
    Answers: request.Answers ?? [],
});

export const mapContextualResponse = (
    response: AiContextualAiResponse,
): ContextualAiTurnResult<AiTemplatePreview | AiPositionDescriptionResponse> => ({
    assistantMessage: response.Rationale
        ?? response.PositionDescription?.AssistantMessage
        ?? response.TemplatePreview?.AssistantMessage,
    contextSummary: response.ContextSummary,
    question: response.QuestionText,
    choices: (response.SuggestedChoices ?? []).map((label, index) => ({ id: response.GapKey ? `${response.GapKey}-${index}` : `choice-${index}`, label, value: label })),
    allowFreeText: response.AllowFreeText,
    canSkip: response.AllowSkip,
    canGenerateNow: response.AllowGenerateNow,
    readyToGenerate: response.State === 'ReadyToGenerate' || response.State === 'Generated',
    result: response.TemplatePreview ?? response.PositionDescription,
    needsCoverageDecision: response.CoverageDecision ? {
        category: response.CoverageDecision.ProficiencyGroup,
        includedCount: response.CoverageDecision.IncludedCount,
        heuristicCount: response.CoverageDecision.RecommendedMinimum,
        coverageFingerprint: response.CoverageDecision.CoverageFingerprint,
        coverageContractVersion: response.CoverageDecision.CoverageContractVersion,
        availableActions: response.CoverageDecision.AvailableActions.filter((action): action is 'AddSuggestedItems' | 'DescribeMissingCoverage' | 'RegenerateCategory' | 'ConfirmSufficient' =>
            ['AddSuggestedItems', 'DescribeMissingCoverage', 'RegenerateCategory', 'ConfirmSufficient'].includes(action)),
        coverageRationale: response.CoverageDecision.QuestionText,
        suggestions: response.CoverageDecision.SuggestedAdditions.map((item, index) => ({ id: item.ExistingProficiencyId ?? `suggestion-${index}`, label: item.Description })),
    } : null,
});
